import { strand } from './draw';
import type { DrawContext } from './draw';
import { toScreen } from './project';
import type { View } from './project';
import type { SkyField } from './field';
import type { EdgeFan } from './fan';
import { skyView } from './sky';
import { DRAW_PAD } from './sky-draw';

/*
 * THE AMBIENT WEB — how few strands it takes to say the field is connected.
 *
 * Ported from `reference/universe-v8/engine.js` — `drawSkyEdges` (1161–1204). Checked against it by
 * comparing recorded draw calls.
 *
 * FIVE GATES, AND EVERY ONE OF THEM IS THE POINT. Their build carries 18,619 ties over 10,000
 * names. Drawing them is neither affordable nor legible — a web that dense is a grey field with
 * discs in it. So a tie has to survive all of:
 *
 *   1  its cell is on screen        the same spatial window everything else uses
 *   2  both its names are resident  a tie to an unloaded tile has nowhere to end
 *   3  it is worth drawing          weighty enough, short enough, or attached to what you are reading
 *   4  neither end has spent out    the fan cap, so a hub cannot swallow the frame
 *   5  the frame has budget left    1,400 strands at the closest band, 700 further out
 *
 * and the ordering matters as much as the gates: the cheap tests come first, and the fan is only
 * charged for strands actually DRAWN, so a tie refused by `strand`'s own geometry costs its ends
 * nothing.
 *
 * THE ONE THING THAT BYPASSES EVERYTHING is the figure the reader has open or is pointing at. Its
 * ties skip the importance gate and the fan cap entirely, because "what is this connected to" is
 * the question being asked, and answering it partially would be worse than not at all.
 */

/** What kind of tie this is, as the third number of each triple records it. */
export const TIE_AMBIENT = 0;
/** A rumour jump: drawn as a leading stub, and only at the closest band. */
export const TIE_FAR = 1;
/** Built by `buildLocalTies` between near neighbours — always drawn thinner, always capped at three. */
export const TIE_LOCAL = 2;

/**
 * What decides which ties are worth drawing. All DATA — every one is calibrated to a roster.
 *
 * `minWeight` is measured against the weight their tiles carry, and `ambientMax` is world units;
 * neither survives a different build unchanged.
 */
export interface EdgePaint {
  /** World units per edge cell — the value the field was bucketed with. */
  edgeCell: number;
  squash: number;
  /** How weighty a name must be for its ambient ties to draw, at the closest band and beyond it. */
  minWeight: { near: number; far: number };
  /**
   * How long an ambient tie may be, IN WORLD UNITS.
   *
   * Their comment: *"world units, so a zoom never changes the set"* — which is the whole reason it
   * is not a screen measurement. A screen-space limit would make the web reshuffle as the reader
   * zoomed, and a web that reshuffles reads as noise rather than as structure.
   */
  ambientMax: number;
}

/** Who the reader is attending to. Their ties bypass every gate but the budget. */
export interface EdgeFocus {
  selected?: number | null;
  hovered?: number | null;
}

/** How many strands a frame may draw, and how many any one name may anchor. */
const BUDGET = { near: 1400, far: 700 };
const FAN_CAP = { near: 5, far: 3 };
/** A local tie is always capped at three, whatever the band. */
const LOCAL_CAP = 3;

/** How bright a strand's head is, by what it is. */
const HEAD = { attended: 0.34, local: 0.15, far: 0.1, ambient: 0.22 };
/** Everything but an attended strand is dimmer away from the closest band. */
const OFF_BAND = 0.7;

/** How thick a strand is drawn. Local ties are finer: there are more of them and they say less. */
const WIDTH = { local: 0.8, other: 0.95 };

/** How much a strand bows away from the straight line between its ends. */
const BEND_MAX = 0.13;
const BEND_SCALE = 26;
/** Clear of each disc by its radius plus this, so the trim reads as a gap rather than a graze. */
const GAP_PAD = 7;

/**
 * Draw the ambient web, and report how many strands it cost.
 *
 * `full` is the closest level-of-detail band, which raises the budget and the fan cap, admits
 * lighter names, and is the only band where a rumour jump is drawn at all.
 */
export function drawSkyEdges(
  ctx: DrawContext,
  field: SkyField,
  view: View,
  paint: EdgePaint,
  fan: EdgeFan,
  stamp: number,
  full: boolean,
  focus: EdgeFocus = {},
): number {
  const v = skyView(view.cam, view.view, paint.squash, DRAW_PAD);
  const sq = paint.squash || 1;
  const W = view.view.width;
  const H = view.view.height;
  const z = view.cam.z;
  const budget = full ? BUDGET.near : BUDGET.far;
  /* A backstop only; the real limit is `ambientMax`, in world units. */
  const maxLen = Math.min(W, H) * 3;
  const minW = full ? paint.minWeight.near : paint.minWeight.far;
  const cap = full ? FAN_CAP.near : FAN_CAP.far;
  const sel = focus.selected ?? null;
  const hov = focus.hovered ?? null;
  let drawn = 0;

  ctx.lineCap = 'round';
  const c0 = (v.x0 / paint.edgeCell) | 0;
  const c1 = (v.x1 / paint.edgeCell) | 0;
  const r0 = (v.y0 / paint.edgeCell) | 0;
  const r1 = (v.y1 / paint.edgeCell) | 0;

  for (let cx = c0; cx <= c1 && drawn < budget; cx++) {
    for (let cy = r0; cy <= r1 && drawn < budget; cy++) {
      const bucket = field.edgeCells.get(`${cx},${cy}`);
      if (!bucket) continue;
      for (let q = 0; q < bucket.length && drawn < budget; q++) {
        const id = bucket[q];
        if (id === undefined) continue;
        const k = id * 3;
        const ia = field.edges[k];
        const ib = field.edges[k + 1];
        const soft = field.edges[k + 2];
        if (ia === undefined || ib === undefined || soft === undefined) continue;
        if (!field.has[ia] || !field.has[ib]) continue;
        if (soft === TIE_FAR && !full) continue;

        const attended = ia === sel || ib === sel || ia === hov || ib === hov;
        const local = soft === TIE_LOCAL;
        if (!attended && !local) {
          if (Math.max(field.w[ia] ?? 0, field.w[ib] ?? 0) < minW) continue;
          if ((field.edgeLength[id] ?? 0) > paint.ambientMax) continue;
        }
        /* The fan is CHECKED here and CHARGED below, so a tie the geometry then refuses costs its
           ends nothing — otherwise a hub would spend its allowance on strands nobody ever saw. */
        if (!attended && !(fan.take(ia, local ? LOCAL_CAP : cap, stamp) && fan.take(ib, local ? LOCAL_CAP : cap, stamp))) continue;

        const ax = field.x[ia] ?? 0;
        const ay = field.y[ia] ?? 0;
        const bx = field.x[ib] ?? 0;
        const by = field.y[ib] ?? 0;
        /* A tie longer than the view is unreadable: you cannot see either end, and it draws as a
           streak across the screen. */
        if (Math.hypot(bx - ax, (by - ay) / sq) * z > maxLen) continue;
        const a = toScreen(view, ax, ay);
        const b = toScreen(view, bx, by);
        if ((a.x < 0 && b.x < 0) || (a.x > W && b.x > W) || (a.y < 0 && b.y < 0) || (a.y > H && b.y > H)) continue;

        const len = field.edgeLength[id] || 1;
        /* Long ties bow less: the bend is a fixed sagitta rather than a fixed proportion, so a
           short tie curves visibly and a long one stays nearly straight. */
        const bend = Math.min(BEND_MAX, BEND_SCALE / len);
        const mx = (ax + bx) / 2 - (by - ay) * bend;
        const my = (ay + by) / 2 + (bx - ax) * bend;
        const m = toScreen(view, mx, my);
        const gapA = Math.max(0.9, Math.min(46, (field.r[ia] ?? 0) * z)) + GAP_PAD;
        const gapB = Math.max(0.9, Math.min(46, (field.r[ib] ?? 0) * z)) + GAP_PAD;
        const head = attended
          ? HEAD.attended
          : (local ? HEAD.local : (soft ? HEAD.far : HEAD.ambient)) * (full ? 1 : OFF_BAND);

        if (strand(ctx, a.x, a.y, m.x, m.y, b.x, b.y, gapA, gapB, head, local ? WIDTH.local : WIDTH.other, soft === TIE_FAR)) {
          drawn++;
          if (!attended) { fan.bump(ia, stamp); fan.bump(ib, stamp); }
        }
      }
    }
  }
  ctx.lineCap = 'butt';
  return drawn;
}
